import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, TriangleAlert, Plus, Trash2, Loader2, Upload, X } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gallery = () => {
  const [visible, setVisible] = useState(false);
  const [showNsfw, setShowNsfw] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const location = useLocation();

  const section = showNsfw ? 'nsfw' : 'sfw';
  const isAdmin = !!adminPin;

  useEffect(() => {
    setAdminPin(localStorage.getItem('sniderwolf_admin_pin') || '');
  }, []);

  // Always reset to SFW when navigating to gallery
  useEffect(() => {
    setShowNsfw(false);
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery?section=${section}`);
      setImages(res.data);
    } catch (err) {
      console.error('Failed to fetch gallery:', err);
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast({ title: 'No image selected', description: 'Please choose an image to upload.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', section);
      formData.append('caption', caption);
      await axios.post(`${API}/gallery`, formData, {
        headers: { 'X-Admin-Pin': adminPin },
      });
      toast({ title: 'Image Added', description: `Added to the ${section.toUpperCase()} gallery.` });
      setShowUpload(false);
      setFile(null);
      setCaption('');
      fetchImages();
    } catch (err) {
      console.error('Upload failed:', err);
      toast({ title: 'Upload Failed', description: 'Could not add the image. Try again.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image from the gallery?')) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: { 'X-Admin-Pin': adminPin } });
      setImages((prev) => prev.filter((img) => img.id !== id));
      toast({ title: 'Deleted', description: 'Image removed from the gallery.' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete image.', variant: 'destructive' });
    }
  };

  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-cyan-300 via-teal-400 to-cyan-400 bg-clip-text text-transparent mb-4 flex items-center justify-center gap-3">
            <Sparkles className="w-12 h-12 text-cyan-400 animate-pulse" />
            {showNsfw ? 'NSFW Gallery' : 'SFW Gallery'}
          </h1>
          <p className="text-xl text-gray-300">
            {showNsfw ? 'Adult content — 18+ only' : 'Browse my past commission work'}
          </p>
        </div>

        {/* Toggle + Admin add */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <button
            data-testid="gallery-toggle-button"
            onClick={() => setShowNsfw(!showNsfw)}
            className={`inline-flex items-center justify-center gap-2 font-medium rounded-md border-2 px-8 py-4 text-lg shadow-2xl transition-all duration-300 hover:scale-105 ${
              showNsfw
                ? 'bg-gradient-to-r from-cyan-600 to-teal-600 text-white border-transparent shadow-cyan-500/30 hover:shadow-cyan-500/50'
                : 'border-red-400 text-red-400 bg-transparent shadow-red-500/30 hover:shadow-red-500/50 hover:bg-red-500/10'
            }`}
          >
            {showNsfw ? (
              <>
                <Sparkles className="w-6 h-6" />
                Back to SFW Gallery
              </>
            ) : (
              <>
                <TriangleAlert className="w-6 h-6" />
                NSFW Gallery (18+)
              </>
            )}
          </button>

          {isAdmin && (
            <button
              data-testid="gallery-add-image-button"
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center justify-center gap-2 font-medium rounded-md px-8 py-4 text-lg text-white bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-600 hover:from-teal-500 hover:via-cyan-500 hover:to-teal-500 shadow-2xl shadow-teal-500/30 hover:shadow-teal-500/50 transition-all duration-300 hover:scale-105"
            >
              <Plus className="w-6 h-6" />
              Add Image
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
            <p className="text-gray-400 mt-3">Loading gallery...</p>
          </div>
        ) : images.length === 0 ? (
          <div
            className={`text-center text-gray-400 py-20 transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="text-xl">
              {showNsfw ? 'NSFW portfolio items coming soon!' : 'Portfolio items coming soon!'}
            </p>
            <p className="text-sm mt-2">Check back later to see my work</p>
          </div>
        ) : (
          <div
            data-testid="gallery-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {images.map((img) => (
              <div
                key={img.id}
                data-testid={`gallery-item-${img.id}`}
                className="group relative rounded-xl overflow-hidden border border-teal-500/30 bg-slate-800/40 backdrop-blur shadow-lg shadow-teal-500/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-teal-500/30"
              >
                <img
                  src={`${API}/gallery/image/${img.id}`}
                  alt={img.caption || 'Gallery artwork'}
                  loading="lazy"
                  className="w-full h-64 object-cover"
                />
                {img.caption && (
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <p className="text-white text-sm font-medium">{img.caption}</p>
                  </div>
                )}
                {isAdmin && (
                  <button
                    data-testid={`gallery-delete-${img.id}`}
                    onClick={() => handleDelete(img.id)}
                    className="absolute top-3 right-3 p-2 rounded-lg bg-red-500/80 text-white opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all duration-300"
                    title="Delete image"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload modal */}
      {showUpload && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !uploading && setShowUpload(false)}
        >
          <div
            data-testid="gallery-upload-modal"
            className="w-full max-w-md rounded-2xl border border-teal-500/40 bg-gradient-to-br from-stone-800/90 via-neutral-800/90 to-slate-800/90 backdrop-blur-xl shadow-2xl shadow-teal-500/30 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">
                Add to {showNsfw ? 'NSFW' : 'SFW'} Gallery
              </h3>
              <button
                onClick={() => !uploading && setShowUpload(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-white block mb-1.5">Image</label>
                <input
                  data-testid="gallery-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-teal-600 file:text-white hover:file:bg-teal-500 file:cursor-pointer cursor-pointer"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-white block mb-1.5">
                  Caption <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  data-testid="gallery-caption-input"
                  className="flex h-9 w-full rounded-md border px-3 py-1 text-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  placeholder="Describe this piece..."
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                />
              </div>
              <button
                data-testid="gallery-upload-submit"
                type="submit"
                disabled={uploading}
                className="inline-flex items-center justify-center gap-2 w-full rounded-md px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-600 hover:from-teal-500 hover:via-cyan-500 hover:to-teal-500 shadow-lg shadow-teal-500/40 transition-all duration-300 hover:scale-105 disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" /> Upload Image
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
